import { createFileRoute, Link } from '@tanstack/react-router';
import { useClasses } from '@/data/classes';
import { useProgram } from '@/data/programs';
import { Badge } from '@/components/ui/Badge';
import { formatTime12 } from '@/lib/time';

export const Route = createFileRoute('/admin/programs/$id')({
  component: ProgramDetail,
});

const titleCase = (value: string) =>
  value
    .split('_')
    .flatMap((part) => part.split('-'))
    .map((part, index) => (index === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ');

function ProgramDetail() {
  const { id } = Route.useParams();
  const program = useProgram(id);
  const classes = useClasses({ programId: id });

  if (program.isLoading) return <p aria-live="polite">Loading…</p>;
  if (program.isError || !program.data) {
    return (
      <p role="alert" className="text-[var(--color-danger)]">
        Program not found.
      </p>
    );
  }

  const p = program.data;

  return (
    <div className="space-y-10">
      <div>
        <Link
          to="/admin/programs"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Programs
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1>{p.name}</h1>
          <Badge tone={p.active ? 'success' : 'neutral'}>{p.active ? 'active' : 'inactive'}</Badge>
        </div>
        <p className="mt-1 text-[var(--color-ink-muted)]">
          {titleCase(p.program_type)} · {titleCase(p.delivery_mode)}
        </p>
      </div>

      <section aria-labelledby="program-details-heading" className="surface-elevated p-6">
        <h2 id="program-details-heading" className="font-medium text-[var(--text-lg)]">
          Details
        </h2>
        <dl className="mt-4 grid gap-4 sm:grid-cols-3">
          <div>
            <dt className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Capacity</dt>
            <dd className="mt-1 font-medium">{p.default_capacity ?? 'Not set'}</dd>
          </div>
          <div>
            <dt className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Default price</dt>
            <dd className="mt-1 font-medium tabular-nums">
              {p.default_price_cents == null
                ? 'Not set'
                : `$${(p.default_price_cents / 100).toFixed(2)}`}
            </dd>
          </div>
          <div>
            <dt className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">Delivery</dt>
            <dd className="mt-1 font-medium">{titleCase(p.delivery_mode)}</dd>
          </div>
        </dl>
        {p.description && <p className="mt-5 text-[var(--color-ink-muted)]">{p.description}</p>}
      </section>

      <aside className="surface border border-[var(--color-border)] p-5">
        <h2 className="font-medium text-[var(--text-base)]">Trial + waiver requirements</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge tone={p.requires_trial ? 'warn' : 'neutral'}>
            {p.requires_trial ? 'Trial required' : 'No trial required'}
          </Badge>
          <Badge tone={p.requires_waiver ? 'warn' : 'success'}>
            {p.requires_waiver ? 'Waiver required' : 'No waiver required'}
          </Badge>
        </div>
      </aside>

      <section aria-labelledby="linked-classes-heading" className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="linked-classes-heading" className="text-[var(--text-xl)]">
            Linked classes
          </h2>
          <Link
            to="/admin/classes/new"
            className="text-[var(--color-accent)] text-[var(--text-sm)] underline-offset-2 hover:underline"
          >
            Add class
          </Link>
        </div>

        {classes.isLoading && <p aria-live="polite">Loading classes…</p>}
        {classes.data?.length === 0 && (
          <p className="text-[var(--color-ink-muted)]">No classes linked to this program yet.</p>
        )}
        {classes.data && classes.data.length > 0 && (
          <ul className="grid gap-2">
            {classes.data.map((c) => (
              <li
                key={c.id}
                className="surface flex flex-wrap items-center justify-between gap-3 p-4"
              >
                <div>
                  <Link
                    to="/admin/classes/$id"
                    params={{ id: c.id }}
                    className="font-medium text-[var(--color-accent)] underline-offset-2 hover:underline"
                  >
                    {c.name}
                  </Link>
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)] capitalize">
                    {c.weekday} · {formatTime12(c.start_time)}–{formatTime12(c.end_time)} · {c.room}
                  </p>
                </div>
                <Badge>{titleCase(c.delivery_mode)}</Badge>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
